import { useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField, type FloatingFieldColors } from "./FloatingField";
import { useStore } from "../store/globalState";
import { useCatalogPayers } from "../hooks/useCatalog";
import {
  catalogPayerDisplayName,
  payerCodesEquivalent,
} from "../utils/payerCode";
import type { IBankAccountDetails } from "../interfaces/payment";
import type { IEstimateOption } from "../services/api";
import {
  bankPrefixMismatch,
  inspectBankAccountNumber,
} from "../utils/bankAccountNumber";
import { BackArrowLight, CloseLight } from "../assets/navIcons";

const MONO_INPUT: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  letterSpacing: "0.04em",
};

function payoutFieldColors(view: {
  background: string;
  title: string;
  bodyCopy: string;
  subtitile: string;
  errorColor?: string;
}): FloatingFieldColors {
  return {
    background: view.background,
    title: view.title,
    bodyCopy: view.bodyCopy,
    inactiveColor: view.subtitile,
    errorColor: view.errorColor,
  };
}

/** Local account length expected per destination country (digits only). */
const ACCOUNT_LENGTH: Record<string, number> = { MX: 18, AR: 22, PE: 20 };
const MAX_ACCOUNT_NUMBER_LENGTH = 34;

const sanitizeAccountNumber = (raw: string, exactLength?: number): string => {
  if (exactLength != null) {
    return raw.replace(/\D/g, "").slice(0, exactLength);
  }
  return raw
    .replace(/\s/g, "")
    .replace(/[^a-z0-9]/gi, "")
    .toUpperCase()
    .slice(0, MAX_ACCOUNT_NUMBER_LENGTH);
};

export interface BankAccountSelection extends IBankAccountDetails {
  /** Pre-quote option pinned to the chosen bank, when one matched. */
  option: IEstimateOption | null;
}

export interface BankAccountDetailsSlotProps {
  originCountryCode?: string | null;
  destinationCountry: string;
  destinationCurrency?: string | null;
  fundingMethod?: string | null;
  options: IEstimateOption[];
  optionsLoading?: boolean;
  value?: IBankAccountDetails | null;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (result: BankAccountSelection) => void;
}

/**
 * Second step after choosing bank reception: catalog bank + account number
 * for a new account. Saved accounts are picked on the first popup.
 */
export const BankAccountDetailsSlot: React.FC<BankAccountDetailsSlotProps> = ({
  originCountryCode,
  destinationCountry,
  destinationCurrency,
  fundingMethod,
  options,
  optionsLoading,
  value,
  onBack,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn, errorColor } = colorConfig;
  const fieldColors = payoutFieldColors(colorConfig);

  const catalogScope = {
    ...(originCountryCode ? { originCountryCode } : {}),
    ...(fundingMethod ? { fundingMethod } : {}),
    ...(destinationCurrency ? { currencyCode: destinationCurrency } : {}),
  };

  const { data: payers, loading } = useCatalogPayers({
    ...catalogScope,
    destinationCountryCode: destinationCountry,
    payoutMethod: "BANK_ACCOUNT",
  });

  const methodOptions = useMemo(
    () => options.filter((o) => o.payoutMethod === "BANK_ACCOUNT"),
    [options],
  );
  const offerablePayerCodes = useMemo(() => {
    const set = new Set<string>();
    for (const o of methodOptions) {
      if (o.payerCode) set.add(o.payerCode);
    }
    return set;
  }, [methodOptions]);

  const banks = useMemo(() => {
    if (optionsLoading) return [];
    if (methodOptions.length === 0) return [];
    const available = (payers ?? []).filter(
      (p) => p.effectiveStatus === "AVAILABLE",
    );
    const scoped =
      offerablePayerCodes.size > 0
        ? available.filter((p) =>
            [...offerablePayerCodes].some((code) =>
              payerCodesEquivalent(code, p.payerCode),
            ),
          )
        : available;
    return scoped
      .map((p) => {
        const name = catalogPayerDisplayName(p.payerCode, payers);
        return name
          ? { id: p.payerCode, name, accountPrefixes: p.accountPrefixes }
          : null;
      })
      .filter(
        (row): row is { id: string; name: string; accountPrefixes: string[] | undefined } =>
          row != null,
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [payers, optionsLoading, methodOptions, offerablePayerCodes]);

  const bankLabel = (code: string): string => {
    const found = banks.find((b) => b.id === code);
    return found?.name ?? "";
  };

  const resolveOption = (
    bankId: string | undefined | null,
  ): IEstimateOption | null => {
    if (!bankId) return null;
    return (
      methodOptions.find((o) => payerCodesEquivalent(o.payerCode, bankId)) ??
      null
    );
  };

  const [bankId, setBankId] = useState<string | null>(value?.bankId ?? null);
  const [accountNumber, setAccountNumber] = useState<string>(
    value?.source === "new" ? (value.accountNumber ?? "") : "",
  );

  const expectedLength = ACCOUNT_LENGTH[destinationCountry.toUpperCase()];
  const identifier = sanitizeAccountNumber(accountNumber, expectedLength);
  const inspection = inspectBankAccountNumber(destinationCountry, identifier);
  const selectedPrefixes = banks.find((b) => b.id === bankId)?.accountPrefixes;
  const mismatch =
    inspection.canContinue &&
    bankPrefixMismatch(inspection.bankCode, selectedPrefixes);
  const banksLoading = Boolean(optionsLoading) || loading;
  const valid =
    Boolean(bankId) &&
    inspection.canContinue &&
    banks.some((b) => b.id === bankId);

  return (
    <section className="payout-details-slot">
      <header className="payout-details-header">
        <button
          type="button"
          className="payout-header-action payout-back-btn"
          aria-label={t("common.back")}
          onClick={onBack}
        >
          <BackArrowLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
        <Title
          title={t("payout.details.BANK_ACCOUNT.title")}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>

      <form
        className="payout-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid || !bankId) return;
          onSubmit({
            source: "new",
            bankId,
            bankName: bankLabel(bankId),
            accountNumber: identifier,
            last4: identifier.slice(-4),
            option: resolveOption(bankId),
          });
        }}
      >
        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.bank")}
            value={bankId ?? ""}
            colors={fieldColors}
          >
            <select
              style={{ color: bodyCopy, cursor: "pointer" }}
              value={bankId ?? ""}
              disabled={banksLoading}
              onChange={(e) => {
                setBankId(e.target.value || null);
              }}
            >
              <option value="" />
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </FloatingField>
          {banksLoading && (
            <span className="payout-hint">{t("payout.fields.payerLoading")}</span>
          )}
          {!banksLoading && banks.length === 0 && (
            <span className="payout-hint">{t("payout.fields.noBanks")}</span>
          )}
        </div>

        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.accountNumber")}
            value={identifier}
            colors={fieldColors}
          >
            <input
              type="text"
              inputMode={expectedLength == null ? "text" : "numeric"}
              maxLength={expectedLength ?? MAX_ACCOUNT_NUMBER_LENGTH}
              autoComplete="off"
              style={{ color: bodyCopy, ...MONO_INPUT }}
              value={identifier}
              onChange={(e) => {
                setAccountNumber(
                  sanitizeAccountNumber(e.target.value, expectedLength),
                );
              }}
            />
          </FloatingField>
          {expectedLength != null && (
            <span className="payout-hint">
              {identifier.length} / {expectedLength}
            </span>
          )}
          {identifier.length > 0 && !inspection.formatOk && (
            <span className="payout-hint" style={{ color: errorColor }}>
              {t("payout.fields.accountNumberInvalid")}
            </span>
          )}
          {mismatch && (
            <span className="payout-hint" style={{ color: errorColor }}>
              {t("payout.fields.bankMismatch")}
            </span>
          )}
        </div>

        <Button
          type="submit"
          size="large"
          disabled={!valid}
          background={backgroundBtn}
          color={textColorBtn}
        >
          {mismatch
            ? t("payout.fields.bankMismatchConfirm")
            : t("common.continue")}
        </Button>
      </form>
    </section>
  );
};
